"use client";
import { useAuthActions } from "@convex-dev/auth/react";
import { api } from "@smart-polling/backend/convex/_generated/api";
import { useMutation, useQuery } from "convex/react";
import { useRouter } from "next/navigation";
import { useEffect, useId, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

type QuestionDraft = {
  type: "single-choice" | "multiple-choice" | "text";
  question: string;
  options?: string[];
  hasReliesOn?: boolean;
  reliesOn?: { questionIndex: number; condition: string };
};

export default function CreatePollPage() {
  const { signIn } = useAuthActions();
  const router = useRouter();
  const createPoll = useMutation(api.polls.create);
  const currentUser = useQuery(api.users.current);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dismissAnonWarning, setDismissAnonWarning] = useState(false);
  const [visibility, setVisibility] = useState<"public" | "private">("public");
  const [allowAnonymous, setAllowAnonymous] = useState(true);
  const [scheduleStart, setScheduleStart] = useState(false);
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [setExpiration, setSetExpiration] = useState(false);
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [questions, setQuestions] = useState<QuestionDraft[]>([]);
  const titleId = useId();
  const descriptionId = useId();
  const settingsId = useId();

  useEffect(() => {
    if (currentUser === null) signIn("anonymous");
  }, [currentUser, signIn]);

  function formatTypeLabel(t: QuestionDraft["type"]): string {
    return t
      .split("-")
      .map((w) => (w ? w[0]?.toUpperCase() + w.slice(1) : w))
      .join(" ");
  }

  function parseCondition(condition: string): { op: "equals" | "contains" | ""; value: string } {
    if (!condition) return { op: "", value: "" };
    const idx = condition.indexOf(":");
    if (idx === -1) return { op: "", value: condition };
    const op = condition.slice(0, idx) as "equals" | "contains";
    const value = condition.slice(idx + 1);
    if (op !== "equals" && op !== "contains") return { op: "", value: condition };
    return { op, value };
  }

  function buildCondition(op: "equals" | "contains", value: string): string {
    return `${op}:${value ?? ""}`;
  }

  function updateQuestion(index: number, update: Partial<QuestionDraft>) {
    setQuestions((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...update };
      return next;
    });
  }

  function addOption(qi: number) {
    setQuestions((prev) => {
      const next = [...prev];
      const opts = next[qi].options ?? [];
      next[qi] = { ...next[qi], options: [...opts, ""] };
      return next;
    });
  }

  function updateOption(qi: number, oi: number, value: string) {
    setQuestions((prev) => {
      const next = [...prev];
      const opts = [...(next[qi].options ?? [])];
      opts[oi] = value;
      next[qi] = { ...next[qi], options: opts };
      return next;
    });
  }

  function addQuestion() {
    setQuestions((prev) => [...prev, { type: "single-choice", question: "", hasReliesOn: false }]);
  }

  function removeQuestion(idx: number) {
    setQuestions((prev) => prev.filter((_, i) => i !== idx));
  }

  async function onSubmit() {
    const sanitized = questions.map((q) => {
      delete q.hasReliesOn;
      return {
        ...q,
        options: q.type === "text" ? [] : (q.options ?? []).filter((s) => s.trim()),
      };
    });
    const startDateMs = scheduleStart && startDate ? startDate.getTime() : undefined;
    const endDateMs = setExpiration && endDate ? endDate.getTime() : undefined;
    const poll = await createPoll({
      title,
      description,
      questions: sanitized,
      visibility,
      isAnonymousAllowed: allowAnonymous,
      startDate: startDateMs,
      endDate: endDateMs,
    });
    router.push(`/polls/${poll}`);
  }

  return (
    <div className="container mx-auto max-w-3xl px-4 py-6">
      <h1 className="mb-4 font-semibold text-2xl">Create a Poll</h1>
      {(currentUser === null || currentUser?.isAnonymous) && !dismissAnonWarning ? (
        <div className="mb-4 flex items-start justify-between gap-4 rounded-md border border-yellow-300 bg-yellow-50 p-3 text-yellow-900 dark:border-yellow-700 dark:bg-yellow-950 dark:text-yellow-200">
          <p className="text-sm">
            Polls created without being signed in can be lost, and you won&apos;t be able to view the results later.
          </p>
          <Button type="button" variant="ghost" onClick={() => setDismissAnonWarning(true)}>
            Dismiss
          </Button>
        </div>
      ) : null}
      <div className="grid gap-6">
        <Card className="grid gap-3 p-4">
          <div className="grid gap-2">
            <Label htmlFor={titleId}>Title</Label>
            <Input id={titleId} value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="grid gap-2">
            <Label htmlFor={descriptionId}>Description</Label>
            <Input id={descriptionId} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="grid gap-4 pt-2">
            <div className="grid gap-2">
              <Label htmlFor={`${settingsId}-visibility`}>Visibility</Label>
              <Select value={visibility} onValueChange={(val) => setVisibility(val as "public" | "private")}>
                <SelectTrigger id={`${settingsId}-visibility`} className="w-full sm:w-48">
                  <SelectValue placeholder="Select visibility" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">Public</SelectItem>
                  <SelectItem value="private">Private</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-muted-foreground text-xs">
                Public polls are discoverable on Explore. Private polls are only accessible via link.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id={`${settingsId}-anon`}
                checked={allowAnonymous}
                onCheckedChange={(checked) => setAllowAnonymous(Boolean(checked))}
              />
              <Label htmlFor={`${settingsId}-anon`} className="leading-none">
                Allow anonymous responses
              </Label>
            </div>

            <div className="grid gap-3">
              <Label>Scheduling</Label>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`${settingsId}-schedule-start`}
                    checked={scheduleStart}
                    onCheckedChange={(checked) => setScheduleStart(Boolean(checked))}
                  />
                  <Label htmlFor={`${settingsId}-schedule-start`} className="leading-none">
                    Schedule start
                  </Label>
                </div>
                {scheduleStart && (
                  <DateTimePicker
                    className="sm:w-64"
                    value={startDate}
                    onChange={(d) => setStartDate(d)}
                    placeholder="Start date/time"
                  />
                )}
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`${settingsId}-expiration`}
                    checked={setExpiration}
                    onCheckedChange={(checked) => setSetExpiration(Boolean(checked))}
                  />
                  <Label htmlFor={`${settingsId}-expiration`} className="leading-none">
                    Set expiration
                  </Label>
                </div>
                {setExpiration && (
                  <div className="flex flex-col gap-1">
                    <DateTimePicker
                      className="sm:w-64"
                      value={endDate}
                      onChange={(d) => setEndDate(d)}
                      placeholder="End date/time"
                    />
                    {scheduleStart && startDate && endDate && endDate.getTime() <= startDate.getTime() ? (
                      <span className="text-destructive text-xs">End must be after start.</span>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>

        <div className="grid gap-4">
          {questions.map((q, qi) => {
            const qId = `${titleId}-q-${qi}`;
            return (
              <Card key={qId} className="grid gap-4 p-4">
                <div className="flex items-center justify-between">
                  <h2 className="font-medium text-lg">Question {qi + 1}</h2>
                  <Button variant="ghost" onClick={() => removeQuestion(qi)}>
                    Remove
                  </Button>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor={qId}>Question</Label>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <Input
                      id={qId}
                      value={q.question}
                      onChange={(e) => updateQuestion(qi, { question: e.target.value })}
                      className="flex-1"
                    />
                    <Select
                      value={q.type}
                      onValueChange={(val) => updateQuestion(qi, { type: val as QuestionDraft["type"] })}
                    >
                      <SelectTrigger size="sm" className="w-full sm:w-48">
                        <SelectValue placeholder="Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="single-choice">{formatTypeLabel("single-choice")}</SelectItem>
                        <SelectItem value="multiple-choice">{formatTypeLabel("multiple-choice")}</SelectItem>
                        <SelectItem value="text">{formatTypeLabel("text")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {q.type !== "text" && (
                  <div className="grid gap-3">
                    <Label>Options</Label>
                    <div className="grid gap-2">
                      {(q.options ?? []).map((opt, oi) => (
                        <Input
                          key={`${qId}-opt-${oi}`}
                          value={opt}
                          onChange={(e) => updateOption(qi, oi, e.target.value)}
                          placeholder={`Option ${oi + 1}`}
                        />
                      ))}
                    </div>
                    <div>
                      <Button variant="outline" size="sm" onClick={() => addOption(qi)}>
                        Add option
                      </Button>
                    </div>
                  </div>
                )}

                {!q.hasReliesOn && qi !== 0 && (
                  <div className="grid gap-2">
                    <Button variant="outline" size="sm" onClick={() => updateQuestion(qi, { hasReliesOn: true })}>
                      Add dependency
                    </Button>
                  </div>
                )}
                {q.hasReliesOn && (
                  <div className="grid gap-2">
                    <div className="flex items-center justify-between gap-2">
                      <Label>Show this question only if...</Label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => updateQuestion(qi, { hasReliesOn: false, reliesOn: undefined })}
                      >
                        Remove dependency
                      </Button>
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                      <Select
                        value={q.reliesOn ? String(q.reliesOn.questionIndex) : ""}
                        onValueChange={(val) => {
                          if (val === "" || val === "no-dependency") {
                            updateQuestion(qi, { reliesOn: undefined });
                            return;
                          }
                          const selectedIdx = Number(val);
                          const prevQuestion = questions[selectedIdx];
                          const defaultCondition =
                            prevQuestion?.type === "text"
                              ? buildCondition("contains", "")
                              : buildCondition("equals", "");
                          updateQuestion(qi, {
                            reliesOn: {
                              questionIndex: selectedIdx,
                              condition: defaultCondition,
                            },
                          });
                        }}
                      >
                        <SelectTrigger className="w-full sm:w-48">
                          <SelectValue placeholder="No dependency" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="no-dependency">No dependency</SelectItem>
                          {questions
                            .map((_, idx) => ({ idx }))
                            .filter(({ idx }) => idx < qi)
                            .map(({ idx }) => (
                              <SelectItem key={`${qId}-dep-${idx}`} value={String(idx)}>
                                Question {idx + 1}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>

                      {(() => {
                        const dependsOnIdx = q.reliesOn?.questionIndex;
                        const prevQuestion =
                          typeof dependsOnIdx === "number" && dependsOnIdx >= 0 ? questions[dependsOnIdx] : undefined;
                        const parsed = parseCondition(q.reliesOn?.condition ?? "");
                        if (!prevQuestion) return null;
                        if (prevQuestion.type === "text") {
                          return (
                            <Input
                              placeholder="Contains..."
                              value={parsed.op === "contains" ? parsed.value : ""}
                              onChange={(e) =>
                                updateQuestion(qi, {
                                  reliesOn: q.reliesOn
                                    ? { ...q.reliesOn, condition: buildCondition("contains", e.target.value) }
                                    : undefined,
                                })
                              }
                              className="flex-1"
                            />
                          );
                        }
                        const optionValues = (prevQuestion.options ?? []).map((s) => s.trim()).filter(Boolean);
                        return (
                          <Select
                            value={parsed.op === "equals" ? parsed.value : ""}
                            onValueChange={(val) =>
                              updateQuestion(qi, {
                                reliesOn: q.reliesOn
                                  ? { ...q.reliesOn, condition: buildCondition("equals", val) }
                                  : undefined,
                              })
                            }
                            disabled={optionValues.length === 0}
                          >
                            <SelectTrigger className="w-full sm:flex-1">
                              <SelectValue
                                placeholder={optionValues.length === 0 ? "No options available" : "Select an option"}
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {optionValues.map((opt, oi) => (
                                <SelectItem key={`${qId}-dep-opt-${oi}`} value={opt}>
                                  {opt}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        );
                      })()}
                    </div>
                    {(() => {
                      const dependsOnIdx = q.reliesOn?.questionIndex;
                      const prevQuestion =
                        typeof dependsOnIdx === "number" && dependsOnIdx >= 0 ? questions[dependsOnIdx] : undefined;
                      if (!prevQuestion) return null;
                      if (prevQuestion.type === "text") {
                        return (
                          <p className="text-muted-foreground text-xs">
                            Show when the selected question contains the given text.
                          </p>
                        );
                      }
                      return (
                        <p className="text-muted-foreground text-xs">
                          Show when the selected question equals the chosen option.
                        </p>
                      );
                    })()}
                  </div>
                )}
              </Card>
            );
          })}

          <div>
            <Button variant="outline" onClick={addQuestion}>
              Add question
            </Button>
          </div>
        </div>

        <div>
          <Button onClick={onSubmit} disabled={!title.trim() || questions.some((q) => !q.question.trim())}>
            Create poll
          </Button>
        </div>
      </div>
    </div>
  );
}
